// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Decoder for the packed tables in `unicode.generated.ts`.
 *
 * Everything here is lazy. The tables are ~360 kB of base-36 delta strings in the bundle; expanding
 * all of them costs a few milliseconds, but a paste of three characters should not pay for the
 * confusables map, so each table inflates on first use and is then memoised.
 *
 * Nothing in this file reaches the network. The data is compiled in at build time by
 * `scripts/build-unicode-tables.mjs`, which is the only script in the repo that downloads anything.
 */

import {
  BLOCK_NAMES,
  BLOCK_RANGES,
  CONFUSABLE_CPS,
  CONFUSABLE_TARGETS,
  DEFAULT_IGNORABLE,
  DEPRECATED,
  EXT_PICTOGRAPHIC,
  GC_NAMES,
  GC_RANGES,
  ID_ALLOWED,
  INTENTIONAL_CPS,
  NONCHARACTER,
  PREPENDED_MARK,
  NAME_CPS,
  NAME_TEXT,
  SCRIPT_LONG,
  SCRIPT_NAMES,
  SCRIPT_RANGES,
  SCX_RANGES,
  SCX_SETS,
  UCD_VERSION,
  CONFUSABLES_DATE,
} from './unicode.generated';

export { UCD_VERSION, CONFUSABLES_DATE };

/* ── unpacking ──────────────────────────────────────────────────────────────────────────────── */

/** "0,1,1,f" — first absolute, the rest gaps. */
export function unpackCodePoints(packed: string): number[] {
  if (!packed) return [];
  const out: number[] = [];
  let prev = 0;
  for (const tok of packed.split(',')) {
    prev += parseInt(tok, 36);
    out.push(prev);
  }
  return out;
}

export interface RangeTable {
  readonly starts: Int32Array;
  readonly ends: Int32Array;
  readonly tags: Int32Array;
}

/** "startDelta.length[.tag]" — deltas are measured from the previous range's END. */
export function unpackRanges(packed: string): RangeTable {
  if (!packed) return { starts: new Int32Array(0), ends: new Int32Array(0), tags: new Int32Array(0) };
  const toks = packed.split(',');
  const starts = new Int32Array(toks.length);
  const ends = new Int32Array(toks.length);
  const tags = new Int32Array(toks.length);
  let prev = 0;
  for (let i = 0; i < toks.length; i++) {
    const parts = toks[i]!.split('.');
    const s = prev + parseInt(parts[0]!, 36);
    const e = s + parseInt(parts[1]!, 36);
    starts[i] = s;
    ends[i] = e;
    tags[i] = parts.length > 2 ? parseInt(parts[2]!, 36) : 0;
    prev = e;
  }
  return { starts, ends, tags };
}

/** Index of the range containing `cp`, or -1. The tables are sorted and non-overlapping. */
export function findRange(t: RangeTable, cp: number): number {
  let lo = 0;
  let hi = t.starts.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (cp < t.starts[mid]!) hi = mid - 1;
    else if (cp > t.ends[mid]!) lo = mid + 1;
    else return mid;
  }
  return -1;
}

function memo<T>(build: () => T): () => T {
  let v: T | undefined;
  let done = false;
  return () => {
    if (!done) {
      v = build();
      done = true;
    }
    return v as T;
  };
}

/* ── tables ─────────────────────────────────────────────────────────────────────────────────── */

const names = memo(() => {
  const cps = unpackCodePoints(NAME_CPS);
  const text = NAME_TEXT.split('\n');
  const m = new Map<number, string>();
  for (let i = 0; i < cps.length; i++) m.set(cps[i]!, text[i]!);
  return m;
});

const gcTable = memo(() => unpackRanges(GC_RANGES));
const gcNames = memo(() => GC_NAMES.split(' '));
const blockTable = memo(() => unpackRanges(BLOCK_RANGES));
const blockNames = memo(() => BLOCK_NAMES.split('\n'));
const scriptTable = memo(() => unpackRanges(SCRIPT_RANGES));
const scriptNames = memo(() => SCRIPT_NAMES.split(' '));
const scxTable = memo(() => unpackRanges(SCX_RANGES));
const scxSets = memo(() => SCX_SETS.split('|').map((s) => s.split(' ')));
const ignorable = memo(() => unpackRanges(DEFAULT_IGNORABLE));
const idAllowed = memo(() => unpackRanges(ID_ALLOWED));
const extPict = memo(() => unpackRanges(EXT_PICTOGRAPHIC));
const deprecated = memo(() => unpackRanges(DEPRECATED));
const noncharacter = memo(() => unpackRanges(NONCHARACTER));
const prepended = memo(() => unpackRanges(PREPENDED_MARK));
const intentional = memo(() => new Set(unpackCodePoints(INTENTIONAL_CPS)));

const confusables = memo(() => {
  const cps = unpackCodePoints(CONFUSABLE_CPS);
  const targets = CONFUSABLE_TARGETS.split('\n');
  const m = new Map<number, string>();
  for (let i = 0; i < cps.length; i++) m.set(cps[i]!, targets[i]!);
  return m;
});

const scriptLong = memo(() => {
  const m = new Map<string, string>();
  for (const line of SCRIPT_LONG.split('\n')) {
    const eq = line.indexOf('=');
    if (eq > 0) m.set(line.slice(0, eq), line.slice(eq + 1));
  }
  return m;
});

/* ── public API ─────────────────────────────────────────────────────────────────────────────── */

/**
 * The Unicode name, where charsight ships one.
 *
 * Names are tabulated only for the code points the UI can actually print — everything flaggable,
 * every confusable and every skeleton target. A CJK ideograph or an unassigned code point returns
 * null and is described by its block instead, which is what a reader wants there anyway.
 */
export function charName(cp: number): string | null {
  return names().get(cp) ?? null;
}

export function generalCategory(cp: number): string {
  const i = findRange(gcTable(), cp);
  return i < 0 ? 'Cn' : gcNames()[gcTable().tags[i]!]!;
}

export function blockOf(cp: number): string {
  const i = findRange(blockTable(), cp);
  return i < 0 ? 'No Block' : blockNames()[blockTable().tags[i]!]!;
}

/** Short script alias, e.g. "Latn". Zzzz for unassigned. */
export function scriptOf(cp: number): string {
  const i = findRange(scriptTable(), cp);
  return i < 0 ? 'Zzzz' : scriptNames()[scriptTable().tags[i]!]!;
}

/** Human-readable script name for a short alias. */
export function scriptDisplayName(short: string): string {
  return scriptLong().get(short) ?? short;
}

/**
 * Script_Extensions, as short aliases.
 *
 * This — not Script — is what UTS #39 resolves a mixed-script check against. U+0640 ARABIC TATWEEL
 * is Script=Common but scx={Adlm Arab Mand Mani Ougr Phlp Rohg Sogd Syrc Thaa Yezi}; using Script
 * would let it pass as script-neutral inside an otherwise Latin string.
 */
export function scriptExtensionsOf(cp: number): string[] {
  const i = findRange(scxTable(), cp);
  if (i >= 0) return scxSets()[scxTable().tags[i]!]!;
  return [scriptOf(cp)];
}

export function isDefaultIgnorable(cp: number): boolean {
  return findRange(ignorable(), cp) >= 0;
}

/**
 * Extended_Pictographic — the predicate the emoji allowlists rest on.
 *
 * "A ZWJ flanked by Extended_Pictographic is legitimate" covers all 1,620 RGI emoji ZWJ sequences
 * without shipping the sequence list, and "a single U+FE0F on an Extended_Pictographic base is a
 * presentation selector" covers all 371 registered emoji variation sequences the same way.
 */
export function isExtendedPictographic(cp: number): boolean {
  return findRange(extPict(), cp) >= 0;
}

export function isDeprecated(cp: number): boolean {
  return findRange(deprecated(), cp) >= 0;
}

export function isNoncharacter(cp: number): boolean {
  return findRange(noncharacter(), cp) >= 0;
}

/**
 * Prepended_Concatenation_Mark. Unicode REQUIRES these to be visible, which is exactly why they are
 * subtracted from Default_Ignorable. They are format characters and they are not hidden text;
 * removing U+0600 ARABIC NUMBER SIGN and its relatives corrupts Arabic.
 */
export function isPrependedMark(cp: number): boolean {
  return findRange(prepended(), cp) >= 0;
}

/** UTS #39 Identifier_Status. Everything outside the Allowed set is Restricted. */
export function isIdentifierAllowed(cp: number): boolean {
  return findRange(idAllowed(), cp) >= 0;
}

/** The confusables.txt prototype for a code point, or null if it is not confusable with anything. */
export function confusableTarget(cp: number): string | null {
  return confusables().get(cp) ?? null;
}

/** True where UTS #39's intentional.txt records a DELIBERATE cross-script lookalike. */
export function isIntentionallyConfusable(cp: number): boolean {
  return intentional().has(cp);
}

/**
 * The UTS #39 skeleton of a string: NFD, map every code point through confusables, NFD again.
 *
 * Two strings are confusable exactly when their skeletons are equal. This is the operation that
 * turns "is `раypal` a lookalike?" into a comparison rather than an opinion.
 */
export function skeleton(s: string): string {
  const first = s.normalize('NFD');
  let mapped = '';
  for (const ch of first) {
    mapped += confusables().get(ch.codePointAt(0)!) ?? ch;
  }
  return mapped.normalize('NFD');
}

/**
 * What a string looks like, spelled in ASCII — which is NOT the same question as the skeleton.
 *
 * `skeleton()` is a comparison key and it is deliberately aggressive: TR39 maps `m` to `rn`, so the
 * skeleton of `аррӏе.com` is `apple.corn`. That is correct, and it is useless to show a human, who
 * will read it as the tool having made a mistake.
 *
 * So for display, a character is only folded when it maps to exactly one ASCII character. Anything
 * else — a multi-character prototype, a non-ASCII prototype — is left alone. `аррӏе.com` then reads
 * back as `apple.com`, which is the sentence the user actually needs: *this is what you thought you
 * were looking at*.
 */
export function asciiLookalike(s: string): string {
  let out = '';
  for (const ch of s) {
    const t = confusables().get(ch.codePointAt(0)!);
    out += t !== undefined && t.length === 1 && t.codePointAt(0)! < 0x80 ? t : ch;
  }
  return out;
}

/**
 * UTS #39 §5.1 resolved script set: the intersection of every character's Script_Extensions,
 * with Common (Zyyy) and Inherited (Zinh) treated as wildcards that match everything.
 *
 * Returns null when the intersection is empty — that is what "mixed-script" means. Returns an empty
 * array when every character was script-neutral (digits, punctuation), which is single-script by
 * definition and must not be reported as mixed.
 */
export function resolvedScriptSet(s: string): string[] | null {
  let acc: Set<string> | null = null;
  for (const ch of s) {
    const cp = ch.codePointAt(0)!;
    const scx = scriptExtensionsOf(cp);
    if (scx.length === 1 && (scx[0] === 'Zyyy' || scx[0] === 'Zinh' || scx[0] === 'Zzzz')) continue;
    if (acc === null) {
      acc = new Set(scx);
      continue;
    }
    const next = new Set<string>();
    for (const x of scx) if (acc.has(x)) next.add(x);
    acc = next;
    if (acc.size === 0) return null;
  }
  return acc === null ? [] : [...acc];
}

/** Every script that actually appears, for reporting which ones were mixed. */
export function scriptsPresent(s: string): string[] {
  const out = new Set<string>();
  for (const ch of s) {
    const sc = scriptOf(ch.codePointAt(0)!);
    if (sc !== 'Zyyy' && sc !== 'Zinh' && sc !== 'Zzzz') out.add(sc);
  }
  return [...out];
}

/** `U+200B`, or `U+1F600` for astral code points. */
export function formatCodePoint(cp: number): string {
  return `U+${cp.toString(16).toUpperCase().padStart(4, '0')}`;
}
